import { useEffect, useRef, type RefObject } from 'react';

// A11Y-1 (go-live audit round-2) — shared focus trap for modals/drawers.
//
// Every `role="dialog" aria-modal="true"` surface in the app made an ARIA
// promise the DOM never enforced: Tab still walked focus out of the dialog
// and behind the scrim. This hook is the one fix, adopted by every real
// modal/drawer (see the call sites listed below). It does four things:
//
//   1. On activate — move focus to `initialFocusRef` if given, else the
//      first focusable element inside the container.
//   2. While active — trap Tab / Shift+Tab so focus cycles inside the
//      container (Tab off the last element wraps to the first; Shift+Tab
//      off the first wraps to the last).
//   3. On Escape — call `onClose` (if provided). Dialogs with bespoke
//      Escape/Enter pairing (e.g. ConfirmDialog's Enter-to-confirm) can
//      still layer their own keydown handler alongside this one; this
//      hook only ever calls `onClose`, never anything else.
//   4. On deactivate/unmount — restore focus to whatever element had it
//      before the dialog opened (almost always the trigger button).
//
// CRITICAL — react-hooks/rules-of-hooks is a build-time ERROR in this repo
// (enforced by `npm run build`). Call this hook UNCONDITIONALLY at the top
// of the component, every render, and gate its behavior with the `active`
// flag — never stash it after an early return (e.g. `if (!open) return
// null;`). The hook itself no-ops internally when `active` is false.
//
// Adopted by: pages/LoginPage.tsx (SsoModal), components/CommandPalette.tsx,
// components/activity/ActivityDrawer.tsx, components/billing/PublishConfirmModal.tsx,
// components/ui/ConfirmDialog.tsx (ConfirmModal).
// Deliberately NOT adopted by components/onboarding/TourStep.tsx — see the
// comment at that file's `role`/`aria-modal` for why (it's a coach-mark with
// an intentionally-operable background, not a real modal).

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    // offsetParent is null for display:none / not-yet-painted elements —
    // filters out hidden tab panels etc. without a layout thrash.
    .filter((el) => el.offsetParent !== null);
}

export interface UseFocusTrapOptions {
  /** Whether the dialog is currently open. Gate behavior with this — never
   *  with an early return before the hook call. */
  active: boolean;
  /** Called on Escape while active. Omit to disable Escape-to-close here
   *  (e.g. when the component already has its own Escape handler). */
  onClose?: () => void;
  /** Focus this element first instead of the first focusable descendant
   *  (e.g. a "Cancel" button on a danger-confirm dialog). */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * @param containerRef Ref to the dialog's VISUAL panel (the box that holds
 *   the focusable controls) — not the full-screen backdrop/overlay div.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  { active, onClose, initialFocusRef }: UseFocusTrapOptions,
): void {
  // Survives across renders without re-triggering the effect; only ever
  // written/read inside the effect below.
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    restoreFocusTo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Defer one tick — portal content (or content behind a just-flipped
    // `active`) may not have painted on this same frame yet.
    const focusTimer = window.setTimeout(() => {
      const target = initialFocusRef?.current ?? getFocusable(container)[0] ?? container;
      target.focus?.();
    }, 0);

    // Arrow function (not a hoisted `function` declaration) so TS retains the
    // `container` non-null narrowing established above inside this closure.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onClose) { e.preventDefault(); onClose(); }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      const focusOutside = !current || !container.contains(current);
      if (e.shiftKey) {
        if (focusOutside || current === first) { e.preventDefault(); last.focus(); }
      } else if (focusOutside || current === last) {
        e.preventDefault(); first.focus();
      }
    };
    // Capture phase so the trap wins even if a child stops propagation.
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      restoreFocusTo.current?.focus?.();
    };
  }, [active, containerRef, onClose, initialFocusRef]);
}
